import React from 'react';
import { Navigate } from 'react-router-dom';

/**
 * Error boundary that catches auth-related errors (401/403) thrown by dashboard
 * components and redirects to /login with a "session expired" message.
 *
 * Wrap dashboard routes with this boundary so a thrown auth error (e.g. from an
 * API call during render) doesn't crash the whole app.
 */
class AuthErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasAuthError: false, redirectPath: null };
  }

  static getDerivedStateFromError(error) {
    const status = error?.status;
    const isAuthError =
      error?.isAuthError === true ||
      status === 401 ||
      status === 403 ||
      (typeof error?.message === 'string' &&
        /unauthorized|forbidden|session expired|authentication required/i.test(error.message));

    if (isAuthError) {
      return { hasAuthError: true, redirectPath: '/login' };
    }
    // Non-auth errors are re-thrown so they can be handled by a generic boundary.
    return { hasAuthError: false, redirectPath: null };
  }

  componentDidCatch(error, errorInfo) {
    // Only log auth errors at debug level; never log tokens.
    if (this.state.hasAuthError) {
      console.error('[AuthErrorBoundary] Auth failure intercepted:', error?.status || error?.message);
    }
  }

  render() {
    if (this.state.hasAuthError) {
      return (
        <Navigate
          to="/login"
          replace
          state={{
            from: window.location,
            sessionExpired: true,
            message: 'Your session has expired. Please log in again.',
          }}
        />
      );
    }
    return this.props.children;
  }
}

export default AuthErrorBoundary;
